import type { DateRangeInterface } from 'webapp-types';
import { TimeUnit } from 'webapp/enums/enums';

function getDateRangeIdFromFormula(
	dateRangeFormula: DateRangeInterface.FormulaInterface,
): DateRangeInterface.Id {
	const rangeTimeUnit: DateRangeInterface.IdTimeUnit = dateRangeFormula.range.timeUnit ?? '',
		rangeMultiplier: DateRangeInterface.IdNumber = dateRangeFormula.range.multiplier ?? '',
		rangeOffset: DateRangeInterface.IdNumber = dateRangeFormula.range.offset ?? '',
		includeFuture: DateRangeInterface.IdBoolean = dateRangeFormula.range.includeFuture ? 1 : '',
		isRangeAllTime: DateRangeInterface.IdBoolean = dateRangeFormula.range.isAllTime ? 1 : '',
		absoluteRangeFrom: DateRangeInterface.IdDate = dateRangeFormula.range.startDate || '',
		absoluteRangeTo: DateRangeInterface.IdDate = dateRangeFormula.range.endDate || '';

	const granulationTimeUnit: DateRangeInterface.IdTimeUnit =
			dateRangeFormula.granulation?.timeUnit ?? '',
		granulationMultiplier: DateRangeInterface.IdNumber =
			dateRangeFormula.granulation?.multiplier ?? '',
		granulationWholeRange: DateRangeInterface.IdBoolean = dateRangeFormula.granulation
			?.isWholeRange
			? 1
			: '',
		granulationCumulative: DateRangeInterface.IdBoolean = dateRangeFormula.granulation
			?.cumulative
			? 1
			: '';

	const compareTimeUnit: DateRangeInterface.IdTimeUnit = dateRangeFormula.offset?.timeUnit ?? '',
		compareOffset: DateRangeInterface.IdNumber = dateRangeFormula.offset?.offset ?? '';

	return `${rangeTimeUnit as string}|${rangeMultiplier as string}|${rangeOffset as string}|${includeFuture as string}|${isRangeAllTime as string}|${absoluteRangeFrom as string}|${absoluteRangeTo as string}|${granulationTimeUnit as string}|${granulationMultiplier as string}|${granulationWholeRange as string}|${granulationCumulative as string}|${compareTimeUnit as string}|${compareOffset as string}`;
}

const getDateRangeFormulaFromIdHelpers = {
	dateRegex: new RegExp('^\\d{4}-\\d{2}-\\d{2}$'), // Unescaped /^\d{4}-\d{2}-\d{2}$/gm
	getPipeCharacterCount: (value: string) => {
		return value.replace(/[^|]/g, '').length;
	},
	isValidTimeUnit: (candidate: number) => {
		return candidate >= TimeUnit.TOTAL && candidate <= TimeUnit.YEAR;
	},
	isValidMultiplier: (candidate: number) => {
		return candidate >= 1;
	},
	parseAndValidateNumber: (value: string, validate?: (value: number) => boolean) => {
		if (value) {
			const candidate = Number(value);

			if (isNaN(candidate) || (validate && !validate(candidate))) {
				return undefined;
			}

			return candidate;
		} else {
			return null;
		}
	},
};

function getDateRangeFormulaFromId(id: DateRangeInterface.Id) {
	const pipeCharacterCount = getDateRangeFormulaFromIdHelpers.getPipeCharacterCount(id);

	if (pipeCharacterCount !== 12) {
		return null;
	}

	const values = id.split('|'),
		rangeFormula: DateRangeInterface.RangeInterface = {};

	const rangeTimeUnit = values[0]
		? getDateRangeFormulaFromIdHelpers.parseAndValidateNumber(
				values[0],
				getDateRangeFormulaFromIdHelpers.isValidTimeUnit,
			)
		: null;
	if (rangeTimeUnit === undefined) {
		return null;
	} else {
		// range_time_unit
		rangeFormula.timeUnit = rangeTimeUnit;
	}

	const rangeMultiplier = values[1]
		? getDateRangeFormulaFromIdHelpers.parseAndValidateNumber(
				values[1],
				getDateRangeFormulaFromIdHelpers.isValidMultiplier,
			)
		: null;
	if (rangeMultiplier === undefined) {
		return null;
	} else {
		// range_multiplier
		rangeFormula.multiplier = rangeMultiplier;
	}

	const rangeOffset = values[2]
		? getDateRangeFormulaFromIdHelpers.parseAndValidateNumber(values[2])
		: null;
	if (rangeOffset === undefined) {
		return null;
	} else {
		// range_offset
		rangeFormula.offset = rangeOffset;
	}

	if (values[3] === '1' || values[3] === '') {
		// include_future
		rangeFormula.includeFuture = !!values[3];
	} else {
		return null;
	}

	if (values[4] === '1' || values[4] === '') {
		// is_range_all_time
		rangeFormula.isAllTime = !!values[4];
	} else {
		return null;
	}

	if (values[5] === '' || getDateRangeFormulaFromIdHelpers.dateRegex.test(values[5]!)) {
		// absolute_range_from
		rangeFormula.startDate = values[5] ? (values[5] as DateRangeInterface.IdDate) : null; // validated by dateRegex
	} else {
		return null;
	}

	if (values[6] === '' || getDateRangeFormulaFromIdHelpers.dateRegex.test(values[6]!)) {
		// absolute_range_to
		rangeFormula.endDate = values[6] ? (values[6] as DateRangeInterface.IdDate) : null;
	} else {
		return null;
	}

	const isValidRangeFormula =
		rangeFormula.isAllTime ||
		(rangeFormula.startDate !== null && rangeFormula.endDate !== null) ||
		(rangeFormula.startDate !== null &&
			rangeFormula.endDate === null &&
			rangeFormula.timeUnit !== null) ||
		(rangeFormula.timeUnit !== null &&
			rangeFormula.multiplier !== null &&
			rangeFormula.offset !== null);

	if (!isValidRangeFormula) {
		return null;
	}

	const dateRangeFormula: DateRangeInterface.FormulaInterface = {
		range: rangeFormula,
	};

	let granulationFormula: Partial<DateRangeInterface.GranulationInterface> | null = {};

	if (values[9] === '1' || values[9] === '') {
		// granulation_whole_range
		granulationFormula.isWholeRange = !!values[9];
	} else {
		granulationFormula = null;
	}

	if (granulationFormula && (values[10] === '1' || values[10] === '')) {
		// granulation_cumulative
		granulationFormula.cumulative = !!values[10];
	} else if (granulationFormula) {
		granulationFormula = null;
	}

	if (granulationFormula?.isWholeRange) {
		// unit and multiplier should be null, return only one number
		granulationFormula.timeUnit = null;
		granulationFormula.multiplier = null;
	} else if (granulationFormula) {
		const granulationTimeUnit = values[7]
			? getDateRangeFormulaFromIdHelpers.parseAndValidateNumber(
					values[7],
					getDateRangeFormulaFromIdHelpers.isValidTimeUnit,
				)
			: undefined;
		if (granulationTimeUnit === undefined) {
			granulationFormula = null;
		} else {
			// granulation_time_unit
			granulationFormula.timeUnit = granulationTimeUnit;
		}

		const granulationMultiplier = granulationFormula
			? values[8]
				? getDateRangeFormulaFromIdHelpers.parseAndValidateNumber(
						values[8],
						getDateRangeFormulaFromIdHelpers.isValidMultiplier,
					)
				: undefined
			: undefined;
		if (granulationFormula && granulationMultiplier === undefined) {
			granulationFormula = null;
		} else if (granulationFormula) {
			// granulation_multiplier
			granulationFormula.multiplier = granulationMultiplier;
		}
	}

	if (granulationFormula) {
		dateRangeFormula.granulation =
			granulationFormula as DateRangeInterface.GranulationInterface;
	}

	let compareFormula: { timeUnit?: number | null; offset?: number | null } | null = {};

	const compareTimeUnit = values[11]
		? getDateRangeFormulaFromIdHelpers.parseAndValidateNumber(
				values[11],
				getDateRangeFormulaFromIdHelpers.isValidTimeUnit,
			)
		: undefined;
	if (compareTimeUnit === undefined) {
		compareFormula = null;
	} else {
		// compare_time_unit
		compareFormula.timeUnit = compareTimeUnit;
	}

	const compareOffset = values[12]
		? getDateRangeFormulaFromIdHelpers.parseAndValidateNumber(values[12])
		: undefined;
	if (compareFormula && compareOffset === undefined) {
		compareFormula = null;
	} else if (compareFormula) {
		// compare_offset
		compareFormula.offset = compareOffset;
	}

	if (compareFormula) {
		dateRangeFormula.offset = compareFormula as DateRangeInterface.OffsetInterface;
	}

	return dateRangeFormula;
}

function isNotValidMetricInsightDateRange(dateRange?: DateRangeInterface.FormulaInterface) {
	return (
		!dateRange?.range ||
		(dateRange &&
			(!dateRange.granulation ||
				dateRange.granulation.isWholeRange ||
				(!dateRange.granulation.isWholeRange &&
					(typeof dateRange.granulation.timeUnit !== 'number' ||
						typeof dateRange.granulation.multiplier !== 'number'))))
	);
}

function isNotValidGoalInsightDateRange(dateRange?: DateRangeInterface.FormulaInterface) {
	return !dateRange?.range || !dateRange.granulation;
}

export {
	getDateRangeIdFromFormula,
	getDateRangeFormulaFromId,
	isNotValidMetricInsightDateRange,
	isNotValidGoalInsightDateRange,
};
